import { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, CheckCircle2, Flag, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CompetitionInsightsPanel } from '@/components/shared/CompetitionInsightsPanel';
import {
  loadCompetitionStandings,
  loadStandingUsers,
  standingUserName,
} from '@/lib/firebase/queries/competitionStandings';
import {
  buildCompetitionDetails,
  type CompetitionDetails,
  type IndividualRow,
  type RankingSlice,
} from '@/lib/competitions/competitionDetails';
import { competitionInsights, type CompetitionInsights } from '@/lib/competitions/competitionInsights';
import { formatAverage, type ContainerStandingRow } from '@/lib/competitions/competitionRanking';
import { formatCompetitionDateRange } from '@/lib/competitions/standingsView';
import type { BeneficiaryCompetition } from '@/lib/competitions/beneficiaryCompetitions';
import type { Competition } from '@/types/competitions';

type TabKey = 'individuals' | 'groups' | 'sections';

interface BeneficiaryCompetitionDetailsDialogProps {
  competition: BeneficiaryCompetition<Competition>;
  organizationId: string;
  currentUserId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * «أفضل 10 والإحصاءات» for a beneficiary (namat#36), like mobile's
 * CompetitionDetailsSheet (namat-mobile#14): participants and how many
 * logged, days left or «انتهت», habits completed, the viewer's own position
 * (individual / group / section), the top 10 of each tab with «···» and the
 * viewer's row below them, and «مؤشرات المسابقة». Names only — never phones
 * or emails. Counts follow the competition's current dates (namat#34).
 */
export function BeneficiaryCompetitionDetailsDialog({
  competition,
  organizationId,
  currentUserId,
  open,
  onOpenChange,
}: BeneficiaryCompetitionDetailsDialogProps) {
  const { t, dir, language } = useLanguage();
  const align = dir === 'rtl' ? 'text-right' : 'text-left';
  const [tab, setTab] = useState<TabKey>('individuals');
  const [details, setDetails] = useState<CompetitionDetails | null>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [insights, setInsights] = useState<CompetitionInsights | null>(null);
  const [failed, setFailed] = useState(false);
  const reqId = useRef(0);
  const unknown = t('dashboard.manager.competitions.insights.unknown');

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    setFailed(false);
    try {
      const standings = await loadCompetitionStandings(competition.id, organizationId, currentUserId, competition);
      const now = new Date();
      const built = buildCompetitionDetails({ currentUserId, standings, window: competition, now });
      const users = new Map(standings.participantUsers);
      const shown = [...built.individuals.rows, ...(built.individuals.me ? [built.individuals.me] : [])].map((r) => r.userId);
      const missing = shown.filter((id) => !users.has(id));
      if (missing.length > 0) (await loadStandingUsers(missing)).forEach((u, id) => users.set(id, u));
      if (mine !== reqId.current) return;
      const allNames = new Map(Array.from(users.entries()).map(([id, u]) => [id, standingUserName(u, language) || unknown]));
      setDetails(built);
      setNames(allNames);
      setInsights(
        competitionInsights({
          logs: standings.logs,
          habits: competition.habits ?? [],
          participantIds: standings.individuals.map((p) => p.userId),
          names: allNames,
          window: competition,
          now,
        })
      );
    } catch (error) {
      console.error('Error loading competition details:', error);
      if (mine === reqId.current) {
        setDetails(null);
        setInsights(null);
        setFailed(true);
      }
    }
  }, [competition, organizationId, currentUserId, language, unknown]);

  useEffect(() => {
    if (!open) return;
    setTab('individuals');
    setDetails(null);
    setInsights(null);
    void load();
  }, [open, load]);

  const label = (key: string, values?: Record<string, string | number>) => t(`dashboard.competition.details.${key}`, values);
  const you = ` (${t('dashboard.competition.you')})`;
  const medal = (rank: number) =>
    rank === 1 ? 'text-yellow-500' : rank === 2 ? 'text-gray-400' : rank === 3 ? 'text-amber-600' : 'text-foreground';

  const line = (key: string, rank: number, name: string, value: number, isMe: boolean, subtitle?: string) => (
    <li
      key={key}
      data-testid={key}
      className={cn('flex items-center gap-3 rounded-lg px-2 py-2', isMe ? 'bg-[#7dda55]/15 border border-[#7dda55]/40' : 'border-b border-muted')}
    >
      <span className={cn('w-7 text-center font-extrabold tabular-nums', medal(rank))}>{rank}</span>
      <div className="flex-1 min-w-0">
        <div className={cn('truncate', align, isMe ? 'font-bold' : 'font-medium')}>
          {name}
          {isMe && you}
        </div>
        {subtitle && <div className={cn('text-xs text-muted-foreground', align)}>{subtitle}</div>}
      </div>
      <span className="font-bold text-[#7dda55] tabular-nums">{value}</span>
    </li>
  );
  const gap = (key: string) => (
    <li key={key} className="text-center text-muted-foreground leading-none" aria-hidden>
      ···
    </li>
  );
  const empty = <p className="text-center text-muted-foreground py-6">{t('dashboard.competition.noParticipants')}</p>;

  const individuals = (slice: RankingSlice<IndividualRow>) => {
    if (slice.total === 0) return empty;
    const row = (r: IndividualRow) =>
      line(`competition-details-row-individuals-${r.userId}`, r.rank, names.get(r.userId) || unknown, r.completed, r.isMe);
    const meOutside = slice.me && !slice.rows.some((r) => r.isMe);
    return (
      <ol className="space-y-1">
        {slice.rows.map(row)}
        {meOutside && slice.me && [gap('competition-details-gap-individuals'), row(slice.me)]}
      </ol>
    );
  };

  const containers = (slice: RankingSlice<ContainerStandingRow>, kind: 'groups' | 'sections') => {
    if (slice.total === 0) return empty;
    const row = (r: ContainerStandingRow) =>
      line(
        `competition-details-row-${kind}-${r.id}`,
        r.rank,
        r.name,
        formatAverage(r.average),
        r.isMe,
        `${r.memberCount} ${t('dashboard.competition.members')} · ${t('dashboard.competition.total')} ${r.completed}`
      );
    const meOutside = slice.me && !slice.rows.some((r) => r.isMe);
    return (
      <>
        <p className={cn('text-xs text-muted-foreground mb-2', align)}>{label('perMemberNote')}</p>
        <ol className="space-y-1">
          {slice.rows.map(row)}
          {meOutside && slice.me && [gap(`competition-details-gap-${kind}`), row(slice.me)]}
        </ol>
      </>
    );
  };

  const stat = (testId: string, Icon: typeof Users, value: string, text: string, sub?: string) => (
    <div data-testid={testId} className="flex-1 rounded-xl bg-[#7dda55]/10 px-2 py-3 text-center">
      <Icon className="h-4 w-4 mx-auto text-[#7dda55]" aria-hidden />
      <div className="text-lg font-extrabold tabular-nums mt-1">{value}</div>
      <div className="text-xs text-muted-foreground">{text}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );

  // The viewer's own standing at a glance, even far outside the top 10.
  const mine = (d: CompetitionDetails) => {
    if (!currentUserId) return null;
    const parts: { key: string; text: string; value: string }[] = [];
    if (d.individuals.me) parts.push({ key: 'individual', text: label('yourPosition'), value: `${d.individuals.me.rank} / ${d.individuals.total}` });
    if (d.groups.me) parts.push({ key: 'group', text: `${t('dashboard.competition.yourGroup')}: ${d.groups.me.name}`, value: `${d.groups.me.rank} / ${d.groups.total}` });
    if (d.sections?.me) parts.push({ key: 'section', text: `${t('dashboard.competition.yourSection')}: ${d.sections.me.name}`, value: `${d.sections.me.rank} / ${d.sections.total}` });
    if (parts.length === 0) return null;
    return (
      <dl data-testid="competition-details-mine" className="rounded-xl bg-[#7dda55]/15 px-3 py-2 space-y-1">
        {parts.map((p) => (
          <div key={p.key} data-testid={`competition-details-mine-${p.key}`} className="flex items-center justify-between gap-2">
            <dt className={cn('flex-1 truncate text-sm font-semibold', align)}>{p.text}</dt>
            <dd className="text-sm font-extrabold tabular-nums" dir="ltr">{p.value}</dd>
          </div>
        ))}
      </dl>
    );
  };

  const body = () => {
    if (!details) {
      return failed ? (
        <p className="text-center text-muted-foreground py-8">{label('loadFailed')}</p>
      ) : (
        <div className="flex items-center justify-center py-10" role="status" aria-label={t('dashboard.competition.loading')}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    const s = details.stats;
    const days = s.phase === 'ended' ? t('dashboard.competition.ended') : s.phase === 'upcoming' ? label('notStarted') : String(s.daysLeft);
    const activeTab: TabKey = tab === 'sections' && !details.sections ? 'individuals' : tab;
    const tabs: { key: TabKey; text: string }[] = [
      { key: 'individuals', text: t('dashboard.competition.individuals') },
      { key: 'groups', text: t('dashboard.competition.groups') },
      ...(details.sections ? [{ key: 'sections' as const, text: t('dashboard.competition.sections') }] : []),
    ];
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          {stat('competition-details-stat-participants', Users, String(s.participants), label('participants'), label('activeParticipants', { count: s.activeParticipants }))}
          {stat('competition-details-stat-days', Flag, days, label('daysLeft'))}
          {stat('competition-details-stat-completed', CheckCircle2, String(s.totalCompleted), label('totalCompleted'))}
        </div>
        {mine(details)}
        <div>
          <h4 className={cn('text-sm font-semibold mb-2', align)}>{label('top')}</h4>
          <Tabs value={activeTab} onValueChange={(v) => setTab(v as TabKey)} className="w-full" dir={dir}>
            <TabsList className={cn('grid w-full', tabs.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
              {tabs.map((tb) => (
                <TabsTrigger key={tb.key} value={tb.key} data-testid={`competition-details-tab-${tb.key}`}>
                  {tb.text}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="individuals" className="mt-3">{individuals(details.individuals)}</TabsContent>
            <TabsContent value="groups" className="mt-3">{containers(details.groups, 'groups')}</TabsContent>
            {details.sections && (
              <TabsContent value="sections" className="mt-3">{containers(details.sections, 'sections')}</TabsContent>
            )}
          </Tabs>
        </div>
        <CompetitionInsightsPanel insights={insights} failed={failed} habitCount={competition.habits?.length ?? 0} />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader>
          <DialogTitle className={cn('pe-6', align)} data-testid="competition-details-title">
            {competition.name}
          </DialogTitle>
          <DialogDescription className={cn('flex items-center gap-2', align)}>
            <Calendar className="h-4 w-4" aria-hidden />
            <span>{formatCompetitionDateRange(competition.startDate, competition.endDate, language)}</span>
          </DialogDescription>
        </DialogHeader>
        {body()}
      </DialogContent>
    </Dialog>
  );
}
